/**
 * CRM contact layer.
 *
 * The profile screen collects one canonical contact record; this module is the
 * only place that decides what a CRM sees. Field names are provider-neutral
 * here — the `crm-sync` function maps them to Zoho or HubSpot.
 */

import { supabase } from "@/integrations/supabase/client";
import { countryByIso } from "@/data/geo";
import { recommendBand } from "@/lib/ageBand";

export type CrmProfile = {
  email: string | null;
  full_name: string | null;
  phone_e164: string | null;
  country: string | null;
  country_code: string | null;
  city: string | null;
  state_region: string | null;
  postal_code: string | null;
  date_of_birth: string | null;
  age: string | null;
  grade: string | null;
  school: string | null;
  headline: string | null;
  guardian_name: string | null;
  guardian_email: string | null;
  guardian_phone: string | null;
  marketing_opt_in: boolean;
  whatsapp_opt_in: boolean;
};

export type CrmContact = {
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  country: string | null;
  countryCode: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  dateOfBirth: string | null;
  age: number | null;
  /** Assessment band this contact is eligible for — the segmentation key. */
  assessmentBand: string | null;
  gradeOrYear: string | null;
  institution: string | null;
  headline: string | null;
  guardianName: string | null;
  guardianEmail: string | null;
  guardianPhone: string | null;
  marketingOptIn: boolean;
  whatsappOptIn: boolean;
  source: string;
};

function splitName(full: string | null): { firstName: string; lastName: string } {
  const parts = (full ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], lastName: parts[0] };
  return { firstName: parts.slice(0, -1).join(" "), lastName: parts[parts.length - 1] };
}

/** Builds the provider-neutral contact payload from a profile row. */
export function buildCrmContact(p: CrmProfile): CrmContact {
  const { firstName, lastName } = splitName(p.full_name);
  const rec = recommendBand(p.date_of_birth, p.age);
  const country = p.country ?? countryByIso(p.country_code)?.name ?? null;
  return {
    email: (p.email ?? "").trim(),
    firstName,
    lastName,
    phone: p.phone_e164 ?? null,
    country,
    countryCode: p.country_code ?? null,
    city: p.city ?? null,
    state: p.state_region ?? null,
    zip: p.postal_code ?? null,
    dateOfBirth: p.date_of_birth ?? null,
    age: rec.age,
    assessmentBand: rec.label,
    gradeOrYear: p.grade ?? null,
    institution: p.school ?? null,
    headline: p.headline ?? null,
    guardianName: p.guardian_name ?? null,
    guardianEmail: p.guardian_email ?? null,
    guardianPhone: p.guardian_phone ?? null,
    marketingOptIn: !!p.marketing_opt_in,
    whatsappOptIn: !!p.whatsapp_opt_in,
    source: "WCC360",
  };
}

/** The fields a CRM record cannot be created without. */
export function missingCrmFields(p: CrmProfile): string[] {
  const missing: string[] = [];
  if (!(p.email ?? "").trim()) missing.push("Email");
  if (!(p.full_name ?? "").trim()) missing.push("Full name");
  if (!p.phone_e164) missing.push("Phone");
  if (!p.country_code) missing.push("Country");
  if (!(p.city ?? "").trim()) missing.push("City");
  return missing;
}

export type CrmSyncResult = {
  ok: boolean;
  provider?: string | null;
  contactId?: string | null;
  message?: string;
};

/** Pushes the signed-in learner's profile to the configured CRM. */
export async function syncProfileToCrm(): Promise<CrmSyncResult> {
  const { data, error } = await supabase.functions.invoke("crm-sync", { body: {} });
  if (error) {
    return { ok: false, message: error.message };
  }
  const res = (data ?? {}) as CrmSyncResult;
  return { ok: !!res.ok, ...res };
}
